import { useRouterState } from "@tanstack/react-router";
// Website chrome for a festival venue in the browser (mirrors RecordWebLayout
// for record shops): a sticky glassy top nav with the festival wordmark, a
// Sign in / My tickets link and a Buy CTA, the page content, and a footer.
// (Marketing sub-page links, Lineup / What's on / Info, are hidden for now
// until that content is ready; the routes still exist.) Inside the native shell
// the app renders as normal; this is browser-only. Themed via the festival accent.
import type { ReactNode } from "react";
import { useVenue } from "../lib/venue";
import { useAuth } from "../lib/auth";
import { resolveFestivalContent } from "../lib/festival-content";

export function FestivalWebLayout({ children }: { children: ReactNode }) {
  const { data } = useVenue();
  const { user } = useAuth();
  const venue = data?.venue;
  const story = resolveFestivalContent(venue);
  const logo = story?.hero?.logo ?? null;
  const accent = story?.theme?.accent;
  const accentFg = story?.theme?.accentFg ?? "#ffffff";
  const displayFont = story?.theme?.displayFont;

  const themeStyle =
    accent || displayFont
      ? ({
          ...(accent ? { "--accent": accent, "--accent-fg": accentFg } : {}),
          ...(displayFont ? { fontFamily: displayFont } : {}),
        } as React.CSSProperties)
      : undefined;

  // Sign-up presale: the site is a single email-capture splash, so the sub-page
  // nav (Lineup / What's on / Info) and Buy-tickets CTA aren't live yet, hide
  // the top nav entirely for a clean landing. In "buy" mode tickets ARE on sale,
  // so the full nav (links + Buy CTA + Sign in / My tickets) comes back.
  const hideNav = !!story?.splash && story.splash.mode !== "buy";
  // Admin pages escape the centred site container, the admin sidebar hugs the
  // far-left edge and the builder uses the full width (AdminLayout owns gutters).
  const adminPath = useRouterState({ select: (st) => st.location.pathname });
  const isAdmin = adminPath === "/admin" || adminPath.startsWith("/admin/");

  return (
    <div style={themeStyle} className="flex min-h-screen flex-col text-[var(--foreground)]">
      {/* Sticky glassy top nav, hidden only during sign-up (splash-only) mode. */}
      {!hideNav && (
        <header className="sticky top-0 z-40 border-b border-white/10 bg-black/50 backdrop-blur-md">
          <div className="mx-auto flex max-w-6xl items-center justify-between gap-4 px-4 py-3">
            <a href="/" className="flex min-w-0 items-center gap-2">
              {logo ? (
                <img src={logo} alt={venue?.name ?? "Festival"} className="h-7 w-auto object-contain md:h-8" />
              ) : (
                <span className="truncate text-lg font-black text-white">{venue?.name ?? "Festival"}</span>
              )}
            </a>
            <div className="flex shrink-0 items-center gap-3">
              {/* Signed-in buyers reach their purchased tickets here (on festival
                  venues /tickets is the buy page, so the wallet lives at
                  /my-tickets); signed-out visitors get a Sign in link. */}
              <a
                href={user ? "/my-tickets" : "/sign-in"}
                className="text-sm font-semibold text-white/80 hover:text-white"
              >
                {user ? "My tickets" : "Sign in"}
              </a>
              <a
                href="/tickets"
                className="rounded-full px-4 py-2 text-sm font-bold"
                style={{ backgroundColor: "var(--accent)", color: "var(--accent-fg)" }}
              >
                Buy tickets
              </a>
            </div>
          </div>
        </header>
      )}

      <main id="top" className={`flex-1 ${isAdmin ? "px-4 py-6 lg:px-0 lg:py-4" : "mx-auto w-full max-w-6xl px-4 py-6 md:py-8"}`}>{children}</main>

      <footer className="border-t border-white/10 bg-black/40">
        <div className="mx-auto flex max-w-6xl flex-col gap-3 px-4 py-8 text-sm text-white/60 md:flex-row md:items-center md:justify-between">
          <span className="font-semibold text-white/80">{venue?.name ?? "Festival"}</span>
          <div className="flex flex-wrap gap-4">
            {venue?.website && (
              <a href={venue.website} target="_blank" rel="noreferrer" className="hover:text-white">Website</a>
            )}
            <a href="/privacy" className="hover:text-white">
              Terms &amp; privacy
            </a>
            <a
              href="https://assemblytickets.com"
              target="_blank"
              rel="noreferrer"
              className="text-white/40 hover:text-white"
            >
              Powered by Assembly
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
}
